import {
  Alert,
  Group,
  GroupItem,
  GroupTitle,
  useFieldLabel,
} from "ra-friendsofbabba";
import {
  ArrayInput,
  BooleanInput,
  Button,
  DateInput,
  FormDataConsumer,
  SimpleFormIterator,
  useMutation,
  useNotify,
  usePermissions,
  useTranslate,
} from "react-admin";
import { Chip, Typography, makeStyles } from "@material-ui/core";
import React, { useMemo } from "react";

import { get } from "lodash";
import moment from "moment";
import { useForm } from "react-final-form";

const lte =
  (source, message = "ra.validation.date.lte") =>
  (value, values) => {
    const target = get(values, source);
    if (value && target && moment(value).isAfter(moment(target))) {
      return message;
    }
  };
const gte =
  (source, message = "ra.validation.date.gte") =>
  (value, values) => {
    const target = get(values, source);
    if (value && target && moment(value).isBefore(moment(target))) {
      return message;
    }
  };

const isValidPeriod = (data, s) => {
  const types = ["public", "private", "foreign"];
  const from = get(data, s("from_date"));
  const to = get(data, s("to_date"));
  const days = moment(to).diff(moment(from), "days") + 1;

  const totalDays = types.reduce((acc, type) => {
    const checked = get(data, s(`has_${type}_activity`));
    if (checked) {
      const from = get(data, s(`${type}_from_date`));
      const to = get(data, s(`${type}_to_date`));
      const days = moment(to).diff(moment(from), "days") + 1;
      return acc + days;
    }
    return acc;
  }, 0);
  return days === totalDays;
};

const validPeriod =
  (type, s, message = "ra.validation.date.period") =>
  (_, values) => {
    const checked = get(values, s(`has_${type}_activity`));
    if (!checked) {
      return;
    }
    if (!isValidPeriod(values, s)) {
      return message;
    }
  };

const useStyles = makeStyles((theme) => ({
  rowInfo: {
    marginTop: theme.spacing(1),
  },
  rowInfoCode: {
    display: "inline-block",
    float: "left",
    marginRight: theme.spacing(1),
    marginBottom: 0,
    paddingBottom: 0,
    fontWeight: "bold",
    fontFamily: "Consolas, Courier New, monospace !important",
    textTransform: "uppercase",
  },
  rowInfoBadge: {
    width: "auto",
    display: "inline-block",
    marginTop: theme.spacing(0),
    marginLeft: theme.spacing(0),
  },
  rowInfoUser: {
    marginTop: 0,
    paddingTop: 0,
    display: "block",
    clear: "both",
  },

  bool: {
    marginTop: theme.spacing(2),
  },
  confirmButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    display: "block",
    clear: "both",
  },
  notified: {
    marginBottom: theme.spacing(1),
  },
}));

const SafeGroupItem = ({ children, basePath, ...props }) => (
  <GroupItem {...props}>{children}</GroupItem>
);

const EDITABLE_STATES = ["draft", "integrations_requested"];
const isEditable = (scopedFormData) =>
  EDITABLE_STATES.includes(get(scopedFormData, "state.code"));

const Toolbar = ({ formData, scopedFormData, s, l }) => {
  const classes = useStyles();
  const [_notified, setNotified] = React.useState(null);
  const notify = useNotify();
  const translate = useTranslate();
  const notified = useMemo(
    () => _notified || get(formData, s("notified")),
    [formData, s, _notified]
  );
  const [update, { loading }] = useMutation(
    {
      type: "post",
      resource: "technical-reports/confirm",
      payload: { data: scopedFormData },
    },
    {
      onSuccess: () => {
        if (notified === null) {
          setNotified(moment().toISOString());
        }
        notify(
          translate("resources.technical-reports.actions.confirm.success")
        );
      },
    }
  );
  const valid = useMemo(() => isValidPeriod(formData, s), [formData, s]);
  return (
    <Group>
      <GroupItem lg={3}>&nbsp;</GroupItem>
      <GroupItem lg={9}>
        {notified ? (
          <Typography variant="body2" className={classes.notified}>
            {translate("resources.technical-reports.fields.notified", {
              date: moment(notified).format("DD/MM/YYYY"),
              time: moment(notified).format("HH:mm"),
            })}
          </Typography>
        ) : (
          <Alert severity="warning">
            {translate("resources.technical-reports.fields.notified.never")}
          </Alert>
        )}
        {isEditable(scopedFormData) && (
          <Button
            className={classes.confirmButton}
            onClick={update}
            disabled={loading || !valid}
            label={l("confirm")}
            variant="outlined"
          />
        )}
      </GroupItem>
    </Group>
  );
};

const TechnicalReportsInput = (props) => {
  const l = useFieldLabel({ resource: "technical-reports" });
  const getLabel = useFieldLabel(props);
  const classes = useStyles();
  const { loaded, permissions } = usePermissions();
  const isTro = loaded && permissions(["tro"]);
  const state = get(props, "record.transaction.state");
  const form = useForm();
  const handleToggle = (checked, type, getSource) => {
    if (!checked) {
      form.batch(() => {
        form.change(getSource(`${type}_from_date`), null);
        form.change(getSource(`${type}_to_date`), null);
      });
    }
  };
  return (
    <Group wrapper {...props}>
      <GroupTitle
        title={getLabel("technical_reports")}
        subTitle={getLabel(`technical_reports.sub.${state}`)}
      />
      <ArrayInput label="">
        <SimpleFormIterator
          disableReordering
          disableRemove={isTro || state !== "waiting_assignments"}
          disableAdd
          TransitionProps={{ enter: false, exit: false }}
        >
          <FormDataConsumer>
            {({ formData, scopedFormData, getSource: s, ...rest }) => (
              <Group wrapper {...rest}>
                <Group>
                  <SafeGroupItem lg={3}>
                    <div className={classes.rowInfo}>
                      <Typography
                        variant="body1"
                        className={classes.rowInfoCode}
                      >
                        {scopedFormData?.research_project?.code}
                      </Typography>
                      <Chip
                        size="small"
                        variant={
                          scopedFormData?.state?.code === "approved"
                            ? "default"
                            : "outlined"
                        }
                        color="primary"
                        label={scopedFormData?.state?.name}
                        className={classes.rowInfoBadge}
                      />
                      <Typography
                        variant="body2"
                        className={classes.rowInfoUser}
                      >
                        {scopedFormData?.user?.name}
                      </Typography>
                    </div>
                  </SafeGroupItem>
                  <GroupItem lg={4} md={4} sm={4} xs={12}>
                    <DateInput
                      label={l("from_date")}
                      source={s("from_date")}
                      validate={[lte(s("to_date")), gte("start"), lte("end")]}
                      disabled={isTro || !isEditable(scopedFormData)}
                    />
                  </GroupItem>
                  <GroupItem lg={4} md={4} sm={4} xs={12}>
                    <DateInput
                      label={l("to_date")}
                      source={s("to_date")}
                      validate={[gte(s("from_date")), gte("start"), lte("end")]}
                      disabled={isTro || !isEditable(scopedFormData)}
                    />
                  </GroupItem>
                </Group>

                {state !== "waiting_assignments" &&
                  ["public", "private", "foreign"]
                    .filter(
                      (type) =>
                        isEditable(scopedFormData) ||
                        get(scopedFormData, `has_${type}_activity`)
                    )
                    .map((type) => (
                      <Group key={type}>
                        <GroupItem lg={3}>
                          <BooleanInput
                            className={classes.bool}
                            label={l(`has_${type}_activity`)}
                            source={s(`has_${type}_activity`)}
                            disabled={!isEditable(scopedFormData)}
                            onChange={(checked) =>
                              handleToggle(checked, type, s)
                            }
                          />
                        </GroupItem>
                        <GroupItem lg={4} md={4} sm={4} xs={12}>
                          <DateInput
                            disabled={
                              !get(scopedFormData, `has_${type}_activity`) ||
                              !isEditable(scopedFormData)
                            }
                            label={l(`${type}_from_date`)}
                            source={s(`${type}_from_date`)}
                            validate={[
                              lte(s(`${type}_to_date`)),
                              validPeriod(type, s),
                            ]}
                          />
                        </GroupItem>
                        <GroupItem lg={4} md={4} sm={4} xs={12}>
                          <DateInput
                            disabled={
                              !get(scopedFormData, `has_${type}_activity`) ||
                              !isEditable(scopedFormData)
                            }
                            label={l(`${type}_to_date`)}
                            source={s(`${type}_to_date`)}
                            validate={[
                              gte(s(`${type}_from_date`)),
                              validPeriod(type, s),
                            ]}
                          />
                        </GroupItem>
                      </Group>
                    ))}

                {state === "confirmed" && (
                  <Toolbar {...{ formData, scopedFormData, s, l }} />
                )}
              </Group>
            )}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </Group>
  );
};

export default TechnicalReportsInput;
